import { prospectArrayFields } from "../constants";

/**
 * Utility functions for drag and drop operations
 */

/**
 * Handle drag and drop for prospect mapping
 */
export const handleProspectDragEnd = (
  result,
  availableIngestionFields,
  setAvailableIngestionFields,
  prospectMappings,
  setProspectMappings,
  getArrayObjectTemplate
) => {
  const { source, destination, draggableId } = result;
  if (!destination) return;

  const startDroppableId = source.droppableId;
  const finishDroppableId = destination.droppableId;

  if (startDroppableId === finishDroppableId && source.index === destination.index) {
    return;
  }

  // Update available ingestion fields
  const newAvailableFields = Array.from(availableIngestionFields);
  const newProspectMappings = Array.from(prospectMappings);

  if (startDroppableId === "available-ingestion") {
    // Remove from available fields
    newAvailableFields.splice(source.index, 1);
  } else {
    // Remove existing mapping
    const existingIndex = newProspectMappings.findIndex(
      (m) => m.prospectField === startDroppableId && m.ingestionField === draggableId
    );
    if (existingIndex !== -1) {
      newProspectMappings.splice(existingIndex, 1);
      newAvailableFields.push(draggableId);
    }
  }

  if (finishDroppableId === "available-ingestion") {
    // Add back to available fields
    newAvailableFields.splice(destination.index, 0, draggableId);
  } else {
    // Create new mapping
    const isArrayField = prospectArrayFields.includes(finishDroppableId);
    const mapping = {
      prospectField: finishDroppableId,
      ingestionField: draggableId,
      isArray: isArrayField,
      ...(isArrayField
        ? getArrayObjectTemplate(finishDroppableId, draggableId)
        : {
            path: draggableId,
            include: "(e) => e",
          }),
    };
    newProspectMappings.push(mapping);
  }

  setAvailableIngestionFields(newAvailableFields);
  setProspectMappings(newProspectMappings);
};

/**
 * Handle drag and drop for enrich consumer mapping
 */
export const handleEnrichConsumerDragEnd = (
  result,
  availableIngestionFieldsEnrich,
  setAvailableIngestionFieldsEnrich,
  enrichConsumerMappings,
  setEnrichConsumerMappings,
  getArrayObjectTemplate
) => {
  const { source, destination, draggableId } = result;
  if (!destination) return;

  const startDroppableId = source.droppableId;
  const finishDroppableId = destination.droppableId;

  if (startDroppableId === finishDroppableId && source.index === destination.index) {
    return;
  }

  // Update available ingestion fields for enrich consumer (separate from prospect)
  const newAvailableFieldsEnrich = Array.from(availableIngestionFieldsEnrich);
  const newEnrichConsumerMappings = Array.from(enrichConsumerMappings);

  if (startDroppableId === "available-ingestion-enrich") {
    // Remove from available fields
    newAvailableFieldsEnrich.splice(source.index, 1);
  } else {
    // Remove existing mapping
    const existingIndex = newEnrichConsumerMappings.findIndex(
      (m) => m.prospectField === startDroppableId && m.ingestionField === draggableId
    );
    if (existingIndex !== -1) {
      newEnrichConsumerMappings.splice(existingIndex, 1);
      newAvailableFieldsEnrich.push(draggableId);
    }
  }

  if (finishDroppableId === "available-ingestion-enrich") {
    // Add back to available fields
    newAvailableFieldsEnrich.splice(destination.index, 0, draggableId);
  } else {
    // Create new mapping
    const isArrayField = prospectArrayFields.includes(finishDroppableId);
    const mapping = {
      prospectField: finishDroppableId,
      ingestionField: draggableId,
      isArray: isArrayField,
      ...(isArrayField
        ? getArrayObjectTemplate(finishDroppableId, draggableId)
        : {
            path: draggableId,
            include: "(e) => e",
          }),
    };
    newEnrichConsumerMappings.push(mapping);
  }

  setAvailableIngestionFieldsEnrich(newAvailableFieldsEnrich);
  setEnrichConsumerMappings(newEnrichConsumerMappings);
};

/**
 * Update available ingestion fields when ingestion mappings change
 */
export const updateAvailableIngestionFields = (
  ingestionMappings,
  prospectMappings,
  enrichConsumerMappings,
  setAvailableIngestionFields,
  setAvailableIngestionFieldsEnrich
) => {
  const ingestionOutputFields = Object.entries(ingestionMappings)
    .filter(([, mapping]) => mapping.include)
    .map(([, mapping]) => mapping.targetField)
    .filter(Boolean);

  // Remove fields that are already mapped in prospect mappings
  const mappedProspectFields = prospectMappings.map((mapping) => mapping.ingestionField);
  const availableProspectFields = ingestionOutputFields.filter(
    (field) => !mappedProspectFields.includes(field)
  );

  // Remove fields that are already mapped in enrich consumer mappings
  const mappedEnrichFields = enrichConsumerMappings.map((mapping) => mapping.ingestionField);
  const availableEnrichFields = ingestionOutputFields.filter(
    (field) => !mappedEnrichFields.includes(field)
  );

  setAvailableIngestionFields(availableProspectFields);
  setAvailableIngestionFieldsEnrich(availableEnrichFields);
};
